import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "./icons";

/* Model + agent filters for the overview (OPSS-421).
 *
 * Options come from the rollups the page has already fetched — the models and
 * agents with traffic in the window — not from a separate options endpoint:
 * `/filters` was removed from the BFF, and its option lists had no readers. That
 * also keeps the choices honest, since every option is guaranteed to have data
 * behind it.
 *
 * Styled against the `.tw-app` layer (var(--text)/(--surface)/(--border-2)) to
 * match the header's timeframe menu — deliberately NOT the design-system
 * DropdownSelect, whose tokens belong to the settings/org surfaces and which is
 * single-select besides.
 */
export function DashboardFilters({
  modelOptions,
  selectedModels,
  onModelsChange,
  agentOptions,
  selectedAgents,
  onAgentsChange,
  onClear,
}: {
  modelOptions: string[];
  selectedModels: string[];
  onModelsChange: (models: string[]) => void;
  /** Agent id → display label (registry name, falling back to the id). */
  agentOptions: Array<{ value: string; label: string }>;
  selectedAgents: string[];
  onAgentsChange: (agentIds: string[]) => void;
  /** Drop every filter in ONE update. Must not be built from the two setters
   *  above: they each rewrite the URL search params, so calling both in one
   *  handler makes the second overwrite the first and only one filter clears. */
  onClear: () => void;
}) {
  const active = selectedModels.length + selectedAgents.length;

  return (
    <div className="flex items-center gap-2">
      <MultiSelect
        name="Model"
        options={modelOptions.map((m) => ({ value: m, label: m }))}
        selected={selectedModels}
        onChange={onModelsChange}
      />
      <MultiSelect
        name="Agent"
        options={agentOptions}
        selected={selectedAgents}
        onChange={onAgentsChange}
      />
      {active > 0 && (
        <button
          onClick={onClear}
          className="px-2 py-1.5 text-[13px] transition-colors hover:text-[var(--text)]"
          style={{ color: "var(--text-muted)" }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

function MultiSelect({
  name,
  options,
  selected,
  onChange,
}: {
  name: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Same dismiss behaviour as the header's TimeframePicker.
  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  // "Model", "Model: claude-opus-5", "Model: 2 selected" — the single-selection
  // case names it rather than counting, since that is the common one.
  const label =
    selected.length === 0
      ? name
      : selected.length === 1
        ? `${name}: ${selected[0]}`
        : `${name}: ${selected.length} selected`;

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={options.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex max-w-[280px] items-center gap-1.5 rounded-[5px] border px-2.5 py-1.5 text-[13px] transition-colors hover:bg-[var(--hover)] disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
        style={{
          borderColor: selected.length ? "var(--text-muted)" : "var(--border)",
          color: selected.length ? "var(--text)" : "var(--text-muted)",
        }}
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={13} className="shrink-0" />
      </button>
      {/* The panel is anchored by its RIGHT edge, like the header's timeframe
          menu. The filter cluster sits at the right of the page header, so a
          panel anchored left (and up to 360px wide) opened straight off the right
          of the viewport and clipped the option names. Right-anchoring grows it
          leftwards into the page instead, which is always the roomy direction
          here. */}
      {open && options.length > 0 && (
        <div
          role="listbox"
          aria-multiselectable
          className="absolute right-0 top-full z-50 mt-1 max-h-[320px] min-w-[240px] max-w-[360px] overflow-y-auto rounded-[5px] bg-[var(--surface)] shadow-lg"
          style={{ border: "1px solid var(--border-2)" }}
        >
          {options.map((o) => {
            const isSelected = selected.includes(o.value);
            return (
              <button
                key={o.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => toggle(o.value)}
                title={o.label}
                className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-[var(--hover)] ${isSelected ? "font-semibold" : ""}`}
                style={{ color: "var(--text)" }}
              >
                <span className="truncate">{o.label}</span>
                {isSelected && (
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--val-crimson)"
                    strokeWidth="2.5"
                    className="shrink-0"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
